import { View, Text, Pressable, ActivityIndicator, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { ArrowUp, ArrowDown } from 'lucide-react-native';
import { higherLowerViewStyles as styles } from './HigherLowerView.styles';
import { PlayerStatCard } from '../PlayerStatCard/PlayerStatCard';
import { HigherLowerResultOverlay } from '../HigherLowerResultOverlay/HigherLowerResultOverlay';
import { WarmupToast } from '../WarmupToast/WarmupToast';
import { useHigherLowerGame } from '../../../src/quiz/hooks/useHigherLowerGame';
import {
  HILO_DAILY_COIN_CAP,
  HILO_COINS_PER_CORRECT,
  HILO_TEST_IDS,
  HILO_COLORS,
} from '../../../src/quiz/constants/higherLower.constants';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';

interface HigherLowerViewProps {
  onMoreGames: () => void;
}

export function HigherLowerView({ onMoreGames }: HigherLowerViewProps) {
  const game = useHigherLowerGame();
  // Phones / small screens: drop the avatar + use shorter buttons so the whole game fits without
  // cards overlapping. Desktop / tablet (wide) keeps the full-size design.
  const { width } = useWindowDimensions();
  const compact = width < 768;

  if (game.isLoading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={WARMUP_COLORS.purple} />
      </View>
    );
  }
  if (game.isError) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{game.errorMessage ?? "Couldn't load the game."}</Text>
        <Pressable onPress={() => game.retry()} style={({ pressed }) => [styles.retryButton, pressed && styles.pressedDim]}>
          <Text style={styles.retryText}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  const { baseline, challenger, reveal } = game;
  const revealing = game.phase === 'revealing';
  const coinsUsed = HILO_DAILY_COIN_CAP - game.coinsLeftToday;
  const buttonsDisabled = game.busy || revealing || game.phase !== 'playing';

  return (
    <View style={styles.container} testID={HILO_TEST_IDS.view}>
      <View style={styles.content}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>Higher or Lower</Text>
          <View style={styles.rewardChip}>
            <View style={styles.coinDot} />
            <Text style={styles.rewardChipText}>{`+${HILO_COINS_PER_CORRECT} each`}</Text>
          </View>
        </View>

        <View style={styles.streakStrip}>
          <Text style={styles.streakText} testID={HILO_TEST_IDS.streak}>
            Streak <Text style={styles.streakNum}>{game.streak}</Text> 🔥
          </Text>
          <View style={styles.streakDivider} />
          <Text style={styles.streakText}>
            Best <Text style={styles.bestNum}>{game.bestRun}</Text>
          </Text>
        </View>

        <View style={styles.field}>
          {baseline ? <PlayerStatCard card={baseline} revealed value={baseline.statValue} variant="baseline" compact={compact} /> : null}

          <View style={styles.vsRow}>
            <View style={styles.vsLine} />
            <View style={styles.vsCircle}><Text style={styles.vsText}>VS</Text></View>
            <View style={styles.vsLine} />
          </View>

          {challenger ? (
            <PlayerStatCard
              card={challenger}
              revealed={revealing}
              value={reveal?.value}
              result={revealing ? (reveal?.correct ? 'correct' : 'wrong') : null}
              variant="challenger"
              compact={compact}
            />
          ) : null}
        </View>

        <View style={styles.coinRow}>
          <View style={styles.coinDot} />
          <Text style={styles.coinProgress} testID={HILO_TEST_IDS.coinProgress}>
            {`${Math.min(coinsUsed, HILO_DAILY_COIN_CAP)} / ${HILO_DAILY_COIN_CAP} coins today`}
          </Text>
          {game.capReached ? (
            <Text style={styles.capBanner} testID={HILO_TEST_IDS.capBanner}>· Record mode 🔥</Text>
          ) : null}
        </View>
      </View>

      <View style={styles.buttonsRow}>
        <Pressable
          testID={HILO_TEST_IDS.higherButton}
          disabled={buttonsDisabled}
          onPress={() => game.onGuess('higher')}
          style={({ pressed }) => [styles.choiceButton, compact && styles.choiceButtonCompact, styles.higherOuter, pressed && styles.pressed, buttonsDisabled && styles.buttonDisabled]}
        >
          <LinearGradient
            colors={[HILO_COLORS.higherTop, HILO_COLORS.higher]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={[styles.choiceFace, compact && styles.choiceFaceCompact]}
          >
            <ArrowUp size={22} color="#0F1720" strokeWidth={2.6} />
            <Text style={[styles.choiceText, styles.higherText]}>Higher</Text>
          </LinearGradient>
        </Pressable>
        <Pressable
          testID={HILO_TEST_IDS.lowerButton}
          disabled={buttonsDisabled}
          onPress={() => game.onGuess('lower')}
          style={({ pressed }) => [styles.choiceButton, compact && styles.choiceButtonCompact, styles.lowerOuter, pressed && styles.pressed, buttonsDisabled && styles.buttonDisabled]}
        >
          <LinearGradient
            colors={[HILO_COLORS.lowerTop, HILO_COLORS.lower]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={[styles.choiceFace, compact && styles.choiceFaceCompact]}
          >
            <ArrowDown size={22} color="#3A0E0E" strokeWidth={2.6} />
            <Text style={[styles.choiceText, styles.lowerText]}>Lower</Text>
          </LinearGradient>
        </Pressable>
      </View>

      <HigherLowerResultOverlay
        visible={game.phase === 'over'}
        runLength={game.finalStreak}
        bestRun={game.bestRun}
        newBest={game.newBest}
        coinsUsedToday={Math.min(HILO_DAILY_COIN_CAP - game.coinsLeftToday, HILO_DAILY_COIN_CAP)}
        onPlayAgain={game.onPlayAgain}
        onMoreGames={onMoreGames}
      />

      <WarmupToast message={game.toast} />

      {/* floating "+N" beat on a paid correct guess */}
      {revealing && reveal?.correct && game.coinsBeat > 0 ? (
        <View pointerEvents="none" style={styles.coinBeat}>
          <Text style={styles.coinBeatText}>{`+${game.coinsBeat}`}</Text>
        </View>
      ) : null}
    </View>
  );
}
